import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  formatPhoneBR,
  LEAD_FILE_KIND_LABELS,
  LEAD_ORIGIN_LABELS,
  LEAD_ORIGINS,
  LEAD_STAGE_LABELS,
  LEAD_STAGES,
  MOTIVOS_PERDA,
  type LeadOrigin,
  type LeadStage,
} from '@agencia/shared';
import { useCurrentUser } from '@/auth/AuthContext';
import { LinkPagamento } from '@/components/LinkPagamento';
import { ListaLinks } from '@/components/ListaLinks';
import { ReservasCliente } from '@/components/ReservasCliente';
import type { LinkPagamentoItem } from '@/lib/pagamentos';
import { Alert, Button } from '@/components/ui';
import { api } from '@/lib/api';
import { ORIGIN_TONE, type Capabilities, type LeadDetail } from '@/lib/crm';
import { IaIcon } from '@/pages/ClientesPage';
import { errorMessage } from '@/lib/errors';
import { formatBRL, formatDate, formatDateTime, parseMoneyInput, toInputMoney } from '@/lib/format';
import { useApi } from '@/lib/useApi';

interface SaleOption {
  id: string;
  numero: number | null;
  clienteNome: string;
  dataVenda: string;
  valorBruto: string;
}

/** Converte "2026-11-03T16:00" (horário de Salvador, digitado) para ISO com fuso. */
const localToIso = (v: string) => `${v}:00-03:00`;
const isoToLocal = (iso: string) => {
  const d = new Date(new Date(iso).getTime() - 3 * 3600_000);
  return d.toISOString().slice(0, 16);
};

/** Cartão do cliente: etapa, retorno, anotações, vendas ligadas e documentos. */
export function LeadPage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const me = useCurrentUser();
  const { data: lead, error, setData } = useApi<LeadDetail>(`/crm/leads/${id}`);
  const { data: caps } = useApi<Capabilities>('/crm/capabilities');
  const { data: links, reload: reloadLinks } = useApi<LinkPagamentoItem[]>(`/crm/payment-links?leadId=${id}`);
  const [msg, setMsg] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function patch(body: Record<string, unknown>, ok?: string) {
    setBusy(true);
    setMsg(null);
    try {
      setData(await api.patch<LeadDetail>(`/crm/leads/${id}`, body));
      if (ok) setMsg({ kind: 'success', text: ok });
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <div className="page">
        <Alert>{error}</Alert>
        <Link to="/clientes">← Clientes</Link>
      </div>
    );
  }
  if (!lead) return <p className="muted">Carregando…</p>;

  const iniciais = lead.nome
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="page narrow-page wide">
      <Link to="/clientes" className="back">
        ← Clientes
      </Link>
      <header className="lead-head">
        <div className="lead-head__id">
          <div className="avatar avatar--lg" aria-hidden="true">
            {iniciais}
          </div>
          <div>
            <h1 className="page-title">{lead.nome}</h1>
            <p className="muted">
              {lead.telefone ? formatPhoneBR(lead.telefone) : 'Sem telefone'} · primeiro contato {formatDateTime(lead.primeiroContatoEm)}
              {lead.consultor && <> · {lead.consultor.nome}</>}
            </p>
            <div className="row tight">
              <span className={`tag-pill tag-pill--${ORIGIN_TONE[lead.origem]}`}>
                Origem: {lead.originLinkNome ?? LEAD_ORIGIN_LABELS[lead.origem]}
                {lead.origemDetalhe && !lead.originLinkNome ? ` · ${lead.origemDetalhe}` : ''}
              </span>
              {lead.digisac && <span className="tag-pill tag-pill--green">Atendimento da Digisac</span>}
            </div>
          </div>
        </div>
      </header>

      {msg && <Alert kind={msg.kind}>{msg.text}</Alert>}

      <Etapa lead={lead} busy={busy} onChange={(etapa, motivoPerda) => void patch({ etapa, ...(motivoPerda ? { motivoPerda } : {}) }, 'Etapa atualizada.')} />

      {(lead.iaMotivo || caps?.ia) && (
        <div className="ia-bar">
          {lead.iaMotivo ? (
            <span className="ia-pill">
              <IaIcon /> Movido pela IA: {lead.iaMotivo}
            </span>
          ) : (
            <span className="muted small">A IA acompanha a conversa e atualiza este cliente sozinha.</span>
          )}
          <div className="row tight">
            {lead.iaMotivo && (
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() =>
                  void api
                    .post<LeadDetail>(`/crm/leads/${lead.id}/undo-ai`)
                    .then((d) => {
                      setData(d);
                      setMsg({ kind: 'success', text: 'Mudança da IA desfeita. Ela não mexe na etapa deste cliente pelas próximas 24 horas.' });
                    })
                    .catch((e: unknown) => setMsg({ kind: 'error', text: errorMessage(e) }))
                }
              >
                Desfazer
              </Button>
            )}
            {caps?.ia && lead.digisac && (
              <Button
                variant="secondary"
                loading={busy}
                onClick={() => {
                  setBusy(true);
                  void api
                    .post<LeadDetail>(`/crm/leads/${lead.id}/analyze`)
                    .then((d) => {
                      setData(d);
                      setMsg({ kind: 'success', text: 'Conversa analisada pela IA.' });
                    })
                    .catch((e: unknown) => setMsg({ kind: 'error', text: errorMessage(e) }))
                    .finally(() => setBusy(false));
                }}
              >
                Analisar conversa agora
              </Button>
            )}
          </div>
        </div>
      )}

      {(params.get('ligarVenda') === '1' || (lead.etapa === 'FECHOU' && lead.sales.length === 0)) && (
        <LigarVenda
          leadId={lead.id}
          onLinked={(d) => {
            setData(d);
            setParams({});
            setMsg({ kind: 'success', text: 'Venda ligada ao cliente.' });
          }}
        />
      )}

      {lead.primeiraMensagem && (
        <section className="card">
          <h2>Primeira mensagem do cliente</h2>
          <blockquote className="quote">{lead.primeiraMensagem}</blockquote>
        </section>
      )}

      <div className="grid-2">
        <Retorno lead={lead} busy={busy} onSave={(retornoEm, retornoNota) => void patch({ retornoEm, retornoNota }, retornoEm ? 'Retorno marcado.' : 'Retorno concluído.')} />
        <Dados lead={lead} busy={busy} onSave={(body) => void patch(body, 'Dados salvos.')} />
      </div>

      <Documentos lead={lead} onChanged={setData} emailLigado={Boolean(caps?.email)} />

      <ReservasCliente key={`${lead.id}-${lead.files.length}`} leadId={lead.id} />

      {caps?.mercadoPago && lead.etapa !== 'PERDEU' && (
        <section className="card">
          <LinkPagamento
            leadId={lead.id}
            telefone={lead.telefone}
            descricaoInicial={lead.resumo ?? ''}
            onCreated={() => {
              void reloadLinks();
              void api.get<LeadDetail>(`/crm/leads/${lead.id}`).then(setData).catch(() => undefined);
            }}
          />
          {links && links.length > 0 && (
            <>
              <h3 className="links-list__title">Links deste cliente</h3>
              <ListaLinks links={links} onChanged={() => void reloadLinks()} mostrarCliente={false} />
            </>
          )}
        </section>
      )}

      <div className="grid-2">
        <section className="card">
          <h2>Vendas ligadas</h2>
          {lead.sales.length === 0 ? (
            <p className="muted small">Nenhuma venda ligada. Quando a venda chegar do Conta Azul, o sistema tenta ligar sozinho.</p>
          ) : (
            <ul className="plain-list">
              {lead.sales.map((s) => (
                <li key={s.id}>
                  <Link to={`/vendas/${s.id}`}>Venda #{s.numero ?? '—'}</Link> · {formatDate(s.dataVenda)} · <strong>{formatBRL(s.valorBruto)}</strong>
                  <button
                    type="button"
                    className="linklike small muted-link"
                    onClick={() =>
                      void api
                        .post<LeadDetail>(`/crm/leads/${lead.id}/sale`, { desligarSaleId: s.id })
                        .then(setData)
                        .catch((e: unknown) => setMsg({ kind: 'error', text: errorMessage(e) }))
                    }
                  >
                    desligar
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
        <Historico lead={lead} meNome={me.nome} onNote={setData} />
      </div>
    </div>
  );
}

function Etapa({ lead, busy, onChange }: { lead: LeadDetail; busy: boolean; onChange: (e: LeadStage, motivo?: string) => void }) {
  const [perdendo, setPerdendo] = useState(false);
  const [motivo, setMotivo] = useState<string>(MOTIVOS_PERDA[0]);
  return (
    <section className="card stage-bar" aria-label="Etapa do funil">
      <div className="stage-bar__steps">
        {LEAD_STAGES.filter((s) => s !== 'PERDEU').map((s) => (
          <button key={s} type="button" className={`stage ${lead.etapa === s ? 'is-on' : ''}`} disabled={busy || lead.etapa === s} onClick={() => onChange(s)}>
            {LEAD_STAGE_LABELS[s]}
          </button>
        ))}
        {lead.etapa === 'PERDEU' ? (
          <span className="stage is-lost">Perdeu · {lead.motivoPerda}</span>
        ) : perdendo ? (
          <span className="row tight">
            <select aria-label="Motivo da perda" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
              {MOTIVOS_PERDA.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
            <Button variant="danger" onClick={() => onChange('PERDEU', motivo)}>
              Confirmar
            </Button>
            <Button variant="ghost" onClick={() => setPerdendo(false)}>
              Cancelar
            </Button>
          </span>
        ) : (
          <Button variant="danger" className="stage-bar__lost" onClick={() => setPerdendo(true)}>
            Marcar como perdido
          </Button>
        )}
      </div>
    </section>
  );
}

function Retorno({ lead, busy, onSave }: { lead: LeadDetail; busy: boolean; onSave: (retornoEm: string | null, nota?: string) => void }) {
  const [quandoV, setQuando] = useState(lead.retornoEm ? isoToLocal(lead.retornoEm) : '');
  const [nota, setNota] = useState(lead.retornoNota ?? '');
  const atrasado = lead.retornoEm && new Date(lead.retornoEm) < new Date();
  return (
    <section className={`card ${lead.retornoEm ? 'card--warm' : ''}`}>
      <h2>Próximo retorno</h2>
      {lead.retornoEm && (
        <p>
          <strong className={atrasado ? 'neg' : undefined}>{formatDateTime(lead.retornoEm)}</strong>
          {atrasado && ' · atrasado'}
          {lead.retornoNota && <span className="muted"> · "{lead.retornoNota}"</span>}
        </p>
      )}
      <div className="field">
        <label htmlFor="ret-quando">Quando</label>
        <input id="ret-quando" type="datetime-local" value={quandoV} onChange={(e) => setQuando(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="ret-nota">Anotação</label>
        <input id="ret-nota" maxLength={300} placeholder='Ex.: "vai confirmar as datas com o marido"' value={nota} onChange={(e) => setNota(e.target.value)} />
      </div>
      <div className="row">
        <Button disabled={!quandoV || busy} onClick={() => onSave(localToIso(quandoV), nota || undefined)}>
          Salvar retorno
        </Button>
        {lead.retornoEm && (
          <Button variant="secondary" disabled={busy} onClick={() => onSave(null)}>
            Retorno feito
          </Button>
        )}
      </div>
    </section>
  );
}

function Dados({ lead, busy, onSave }: { lead: LeadDetail; busy: boolean; onSave: (body: Record<string, unknown>) => void }) {
  const [valor, setValor] = useState(lead.valorEstimado ? toInputMoney(lead.valorEstimado) : '');
  const [resumo, setResumo] = useState(lead.resumo ?? '');
  const [origem, setOrigem] = useState<LeadOrigin>(lead.origem);
  const [detalhe, setDetalhe] = useState(lead.origemDetalhe ?? '');
  const [email, setEmail] = useState(lead.email ?? '');
  const [erro, setErro] = useState<string | null>(null);
  function salvar(e: FormEvent) {
    e.preventDefault();
    const v = valor.trim() ? parseMoneyInput(valor) : null;
    if (valor.trim() && !v) return setErro('Valor inválido. Use o formato 1.234,56.');
    setErro(null);
    onSave({ valorEstimado: v, resumo: resumo.trim() || null, email: email.trim() || null, ...(origem !== lead.origem || detalhe !== (lead.origemDetalhe ?? '') ? { origem, origemDetalhe: detalhe || undefined } : {}) });
  }
  return (
    <form className="card" onSubmit={salvar}>
      <h2>Sobre a viagem</h2>
      {erro && <Alert>{erro}</Alert>}
      <div className="field">
        <label htmlFor="d-resumo">Resumo</label>
        <input id="d-resumo" maxLength={300} placeholder="Ex.: SSA ⇄ Orlando · 15 a 25/01 · 4 pessoas" value={resumo} onChange={(e) => setResumo(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="d-email">E-mail do cliente</label>
        <input id="d-email" type="email" placeholder="cliente@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="d-valor">Valor da cotação (R$)</label>
        <input id="d-valor" inputMode="decimal" placeholder="0,00" value={valor} onChange={(e) => setValor(e.target.value)} />
      </div>
      <div className="grid-form">
        <div className="field">
          <label htmlFor="d-origem">Origem</label>
          <select id="d-origem" value={origem} onChange={(e) => setOrigem(e.target.value as LeadOrigin)}>
            {LEAD_ORIGINS.map((o) => (
              <option key={o} value={o}>
                {LEAD_ORIGIN_LABELS[o]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="d-det">Detalhe da origem</label>
          <input id="d-det" maxLength={200} placeholder="Ex.: indicação de Ana Lúcia" value={detalhe} onChange={(e) => setDetalhe(e.target.value)} />
        </div>
      </div>
      <Button type="submit" variant="secondary" loading={busy}>
        Salvar
      </Button>
    </form>
  );
}

function LigarVenda({ leadId, onLinked }: { leadId: string; onLinked: (d: LeadDetail) => void }) {
  const { data: vendas } = useApi<SaleOption[]>(`/crm/leads/${leadId}/linkable-sales`);
  const [saleId, setSaleId] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  return (
    <section className="card highlight">
      <h2>Qual venda é deste cliente?</h2>
      <p className="muted small">Ligar a venda conta esta conversão no relatório e guarda os vouchers na viagem certa.</p>
      {erro && <Alert>{erro}</Alert>}
      {!vendas ? (
        <p className="muted">Carregando vendas…</p>
      ) : vendas.length === 0 ? (
        <p className="muted small">Nenhuma venda recente sem cliente. Se a venda ainda não chegou do Conta Azul, o sistema liga sozinho quando chegar.</p>
      ) : (
        <div className="row">
          <select aria-label="Venda" value={saleId} onChange={(e) => setSaleId(e.target.value)}>
            <option value="">Escolha a venda…</option>
            {vendas.map((v) => (
              <option key={v.id} value={v.id}>
                #{v.numero ?? '—'} · {formatDate(v.dataVenda)} · {v.clienteNome} · {formatBRL(v.valorBruto)}
              </option>
            ))}
          </select>
          <Button
            disabled={!saleId}
            onClick={() =>
              void api
                .post<LeadDetail>(`/crm/leads/${leadId}/sale`, { saleId })
                .then(onLinked)
                .catch((e: unknown) => setErro(errorMessage(e)))
            }
          >
            Ligar venda
          </Button>
        </div>
      )}
    </section>
  );
}

function Documentos({ lead, onChanged, emailLigado }: { lead: LeadDetail; onChanged: (d: LeadDetail) => void; emailLigado: boolean }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [enviarEmail, setEnviarEmail] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return setErro('O arquivo precisa ter até 10 MB.');
    setErro(null);
    setEnviando(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
        r.onerror = () => reject(new Error('não foi possível ler o arquivo'));
        r.readAsDataURL(file);
      });
      onChanged(await api.post<LeadDetail>(`/crm/leads/${lead.id}/files`, { nome: file.name, mimeType: file.type || 'application/octet-stream', base64 }));
    } catch (err) {
      setErro(errorMessage(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <section className="card">
      <div className="card__head">
        <div>
          <h2>Documentos e viagens</h2>
          <p className="muted small">Criptografados · só o consultor do cliente e a gestão veem · cada download fica registrado.</p>
        </div>
        <div className="row tight">
          {lead.files.length > 0 && (
            <Button onClick={() => setEnviarEmail((v) => !v)} disabled={!emailLigado} title={emailLigado ? undefined : 'O envio de e-mails ainda não foi configurado'}>
              Enviar vouchers por e-mail
            </Button>
          )}
          <Button variant="secondary" loading={enviando} onClick={() => fileRef.current?.click()}>
            + Anexar arquivo
          </Button>
        </div>
        <input ref={fileRef} type="file" className="sr-only" accept="application/pdf,image/*" onChange={(e) => void enviar(e)} aria-label="Anexar arquivo" />
      </div>
      {erro && <Alert>{erro}</Alert>}
      {enviarEmail && (
        <EnviarEmail
          lead={lead}
          onClose={() => setEnviarEmail(false)}
          onSent={async () => {
            setEnviarEmail(false);
            onChanged(await api.get<LeadDetail>(`/crm/leads/${lead.id}`));
          }}
        />
      )}
      {lead.files.length === 0 ? (
        <p className="muted small">Nenhum arquivo ainda. Vouchers e bilhetes em PDF que o atendente enviar pela Digisac aparecem aqui sozinhos. Prints e imagens não são guardados.</p>
      ) : (
        <ul className="file-list">
          {lead.files.map((f) => (
            <li key={f.id}>
              <span className="file-list__icon" aria-hidden="true">
                {f.mimeType === 'application/pdf' ? 'PDF' : 'IMG'}
              </span>
              <div className="file-list__info">
                <a href={`/api/crm/files/${f.id}`}>{f.nome}</a>
                <span className="muted small">
                  {LEAD_FILE_KIND_LABELS[f.tipo]} · {(f.tamanho / 1024).toFixed(0)} KB ·{' '}
                  {f.origem === 'DIGISAC' ? `salvo da Digisac${f.enviadoPor ? ` · enviado por ${f.enviadoPor}` : ''}` : `anexado por ${f.enviadoPor ?? '—'}`} · {formatDateTime(f.createdAt)}
                  {f.enviadoEmailEm && <> · <strong className="ok-text">enviado por e-mail em {formatDateTime(f.enviadoEmailEm)}</strong></>}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Historico({ lead, onNote }: { lead: LeadDetail; meNome: string; onNote: (d: LeadDetail) => void }) {
  const [texto, setTexto] = useState('');
  const [salvando, setSalvando] = useState(false);
  useEffect(() => setTexto(''), [lead.id]);
  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (!texto.trim()) return;
    setSalvando(true);
    try {
      await api.post(`/crm/leads/${lead.id}/notes`, { texto });
      onNote(await api.get<LeadDetail>(`/crm/leads/${lead.id}`));
      setTexto('');
    } finally {
      setSalvando(false);
    }
  }
  return (
    <section className="card">
      <h2>Histórico</h2>
      <form className="row" onSubmit={(e) => void salvar(e)}>
        <input className="grow" aria-label="Nova anotação" placeholder="Escrever uma anotação…" maxLength={500} value={texto} onChange={(e) => setTexto(e.target.value)} />
        <Button type="submit" variant="secondary" loading={salvando}>
          Anotar
        </Button>
      </form>
      <ol className="timeline">
        {lead.events.map((e) => (
          <li key={e.id} className={`timeline__item timeline__item--${e.tipo.toLowerCase()}`}>
            <strong>{e.descricao}</strong>
            <span className="muted small">
              {formatDateTime(e.createdAt)} · {e.por ?? 'sistema'}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function EnviarEmail({ lead, onClose, onSent }: { lead: LeadDetail; onClose: () => void; onSent: () => Promise<void> }) {
  const [para, setPara] = useState(lead.email ?? '');
  const [selecionados, setSelecionados] = useState<string[]>(lead.files.filter((f) => f.tipo !== 'DOCUMENTO' && !f.enviadoEmailEm).map((f) => f.id));
  const [localizador, setLocalizador] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const corpo = () => ({ fileIds: selecionados, localizador: localizador.trim() || undefined, mensagem: mensagem.trim() || undefined });

  async function verPrevia() {
    setErro(null);
    try {
      setPreview(await api.post<{ subject: string; html: string }>(`/crm/leads/${lead.id}/email-preview`, corpo()));
    } catch (err) {
      setErro(errorMessage(err));
    }
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (!window.confirm(`Enviar o e-mail de compra concluída para ${para}?`)) return;
    setEnviando(true);
    setErro(null);
    try {
      await api.post(`/crm/leads/${lead.id}/email-vouchers`, { para, ...corpo() });
      await onSent();
    } catch (err) {
      setErro(errorMessage(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="email-panel" onSubmit={(e) => void enviar(e)}>
      <h3>E-mail de compra concluída</h3>
      {erro && <Alert>{erro}</Alert>}
      <div className="grid-form">
        <div className="field">
          <label htmlFor="em-para">Para</label>
          <input id="em-para" type="email" required value={para} onChange={(e) => setPara(e.target.value)} placeholder="cliente@email.com" />
        </div>
        <div className="field">
          <label htmlFor="em-loc">Localizador (opcional)</label>
          <input id="em-loc" maxLength={20} value={localizador} onChange={(e) => setLocalizador(e.target.value)} placeholder="Ex.: XKTR4P" />
        </div>
      </div>
      <fieldset className="email-panel__files">
        <legend>Arquivos anexados</legend>
        {lead.files.map((f) => (
          <label key={f.id} className="check">
            <input
              type="checkbox"
              checked={selecionados.includes(f.id)}
              onChange={(e) => setSelecionados((s) => (e.target.checked ? [...s, f.id] : s.filter((x) => x !== f.id)))}
            />
            {f.nome} <span className="muted small">({LEAD_FILE_KIND_LABELS[f.tipo]})</span>
          </label>
        ))}
      </fieldset>
      <div className="field">
        <label htmlFor="em-msg">Mensagem pessoal (opcional)</label>
        <textarea id="em-msg" rows={3} maxLength={1000} value={mensagem} onChange={(e) => setMensagem(e.target.value)} placeholder="Ex.: Boa viagem! Qualquer coisa, estou à disposição." />
      </div>
      <p className="muted small">O resumo da viagem ({lead.resumo ?? 'não preenchido'}) entra no e-mail. As respostas do cliente vão para o seu e-mail.</p>
      <div className="row">
        <Button type="submit" loading={enviando} disabled={!selecionados.length || !para}>
          Enviar e-mail
        </Button>
        <Button type="button" variant="secondary" disabled={!selecionados.length} onClick={() => void verPrevia()}>
          Ver prévia
        </Button>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
      </div>
      {preview && (
        <div className="email-preview">
          <p className="small">
            <strong>Assunto:</strong> {preview.subject}
          </p>
          <iframe title="Prévia do e-mail" srcDoc={preview.html} sandbox="" />
        </div>
      )}
    </form>
  );
}
